import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Target, Brain, Star, AlertTriangle,
  FileText, PenTool, Youtube,
  ChevronDown, X,
  MessageCircle, Sparkles
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  AA01: {
    scene: '🎮 게임 개발 스튜디오. 기획자가 공격력 공식을 설명한다.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍💼', name: '기획자', text: '이 게임의 공격력 공식은 "레벨의 제곱 × 3"이에요. 식으로 쓰면 어떻게 될까요?' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: '3x² 요! x가 레벨이면 x²에 3을 곱한 거잖아요.' },
      { speaker: 'teacher', emoji: '👩‍💼', name: '기획자', text: '정확해요! 3x²에서 3이 계수, x²의 지수는 2예요. 이게 바로 단항식이에요.' },
      { speaker: 'friend', emoji: '🤔', name: '민호', text: '그럼 차수는요? 3이 차수인가요?' },
      { speaker: 'teacher', emoji: '👩‍💼', name: '기획자', text: '아니에요! 차수는 지수예요. 3x²의 차수는 2. 계수 3은 단지 앞에 곱해진 수죠.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '단항식: 수 또는 문자의 곱으로 이루어진 식 | 계수: 문자 앞의 수 | 차수: 문자 지수의 합' },
    ],
    reveal: '단항식 3x²에서: 계수=3, 차수=2 | 차수 = 지수의 합 (여러 문자면 모두 더함)',
    revealEmoji: '📐',
  },
  AA02: {
    scene: '🛒 편의점 알바. 물건 재고를 계산하는 날.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지수', text: '사과 3개, 오렌지 2개, 사과 4개를 정리하면 사과는 총 7개네요. 동류항처럼요!' },
      { speaker: 'teacher', emoji: '👨‍🏫', name: '점장님', text: '맞아요! 수학에서도 같은 문자·같은 차수의 항끼리만 더할 수 있어요. 그게 동류항이에요.' },
      { speaker: 'friend', emoji: '🤔', name: '민호', text: '그럼 3x² + 2x는요? 더할 수 있나요?' },
      { speaker: 'teacher', emoji: '👨‍🏫', name: '점장님', text: '안 돼요! x²과 x는 차수가 달라서 동류항이 아니에요. 사과와 오렌지를 합칠 수 없는 것처럼요.' },
      { speaker: 'student', emoji: '😄', name: '지수', text: '아, 5x + 3x = 8x는 되지만 5x² + 3x는 그냥 5x² + 3x로 두어야 하는군요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '동류항: 문자와 지수가 모두 같은 항 | 동류항끼리만 계수를 더하거나 뺄 수 있음' },
    ],
    reveal: '동류항 조건: 문자 종류 + 지수 모두 같아야 함 | 계수는 달라도 OK | 동류항: 5x와 −3x → (5−3)x = 2x',
    revealEmoji: '🛒',
  },
  AA03: {
    scene: '🔬 화학 실험실. 분자식을 계산하는 연구원.',
    lines: [
      { speaker: 'teacher', emoji: '👩‍🔬', name: '연구원', text: 'x²y라는 분자가 3개 있으면 어떻게 표현할까요?' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: '3 곱하기 x²y니까... 3x²y요! 계수끼리 곱하고 지수는 더하면 되나요?' },
      { speaker: 'teacher', emoji: '👩‍🔬', name: '연구원', text: '맞아요! 같은 문자끼리 곱하면 지수를 더해요. x² × x³ = x⁵처럼요. xᵐ × xⁿ = xᵐ⁺ⁿ!' },
      { speaker: 'friend', emoji: '😮', name: '민호', text: '그럼 나누기는요? x⁵ ÷ x² = x³ 인가요?' },
      { speaker: 'teacher', emoji: '👩‍🔬', name: '연구원', text: '정확해요! 나눌 때는 지수를 빼요. xᵐ ÷ xⁿ = xᵐ⁻ⁿ. 이게 지수 법칙의 핵심이에요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '곱셈: xᵐ × xⁿ = xᵐ⁺ⁿ | 나눗셈: xᵐ ÷ xⁿ = xᵐ⁻ⁿ | 거듭제곱: (xᵐ)ⁿ = xᵐⁿ' },
    ],
    reveal: '지수 법칙: 곱하면 지수 더하기, 나누면 지수 빼기, 거듭제곱이면 지수 곱하기',
    revealEmoji: '🔬',
  },
  AA04: {
    scene: '🏗️ 건축 현장. 넓이를 계산하는 설계사.',
    lines: [
      { speaker: 'teacher', emoji: '👷', name: '설계사', text: '가로가 (x+2)m, 세로가 3m인 방의 넓이는요?' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: '3 × (x+2) = 3x + 6 이에요! 분배법칙을 쓰면 되잖아요.' },
      { speaker: 'teacher', emoji: '👷', name: '설계사', text: '완벽해요! a(b+c) = ab + ac. 이걸 다항식에 활용하면 전개가 되죠.' },
      { speaker: 'friend', emoji: '🤔', name: '민호', text: '그럼 (2x+1)+(3x−4)는요? 그냥 동류항끼리 더하면 되나요?' },
      { speaker: 'teacher', emoji: '👷', name: '설계사', text: '맞아요! 2x+3x=5x, 1+(−4)=−3이니까 5x−3. 뺄셈은 뒤 괄호 부호를 조심하세요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '다항식 덧셈·뺄셈: 동류항끼리 계산 | 뺄셈: 빼는 식의 모든 부호 반전 후 더하기' },
    ],
    reveal: '분배법칙: a(b+c)=ab+ac | 뺄셈: A−(B+C) = A−B−C (부호 반전!) | 동류항끼리 정리',
    revealEmoji: '🏗️',
  },
  AA05: {
    scene: '🎨 디자인 스튜디오. 식의 값을 활용하는 디자이너.',
    lines: [
      { speaker: 'teacher', emoji: '🎨', name: '디자이너', text: '색상 채도 공식이 2x²−3x+1이에요. x=2일 때 채도 값이 얼마일까요?' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: '2를 대입하면... 2(4)−3(2)+1 = 8−6+1 = 3이에요!' },
      { speaker: 'teacher', emoji: '🎨', name: '디자이너', text: '맞아요! 대입할 때 음수는 꼭 괄호를 쓰세요. x=−1이면 2(−1)²−3(−1)+1 = 2+3+1 = 6이에요.' },
      { speaker: 'friend', emoji: '😄', name: '민호', text: '괄호 없이 쓰면 2×−1²이 되어서 −2가 되는 실수를 할 수 있겠군요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '식의 값: 문자에 수를 대입해 계산 | 음수 대입 시 반드시 괄호 사용!' },
    ],
    reveal: '식의 값 계산: 대입 → 계산 순서 | 음수 대입: 반드시 괄호! x=−2 → (−2) 로 쓰기',
    revealEmoji: '🎨',
  },
}

const STEPS = [
  {
    id: 'AA01',
    title: '단항식의 뜻과 차수',
    subtitle: '계수·차수·단항식을 이해한다',
    icon: '1️⃣',
    color: 'bg-indigo-500',
    lightColor: 'bg-indigo-50',
    borderColor: 'border-indigo-300',
    textColor: 'text-indigo-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['단항식', '계수', '차수'],
    objective: '단항식의 뜻을 알고, 계수와 차수를 구할 수 있다.',
    concept: `**단항식(Monomial)**이란 수 또는 문자의 곱으로만 이루어진 식입니다.

📌 단항식의 구성
• 계수: 문자 앞에 곱해진 수 (숫자 부분)
• 차수: 문자 지수의 합
• 예: 3x²y에서 계수=3, x의 지수=2, y의 지수=1 → 차수=3

📌 차수별 분류
| 차수 | 이름 | 예 |
|------|------|-----|
| 1    | 일차 | 5x, −2y |
| 2    | 이차 | 3x², x²y (x의 지수만) |
| 3    | 삼차 | 2x²y (x²y → 2+1=3) |

📌 단항식 vs 다항식
• 단항식: 항이 1개 → 3x², −5ab
• 다항식: 항이 2개 이상 → 3x²+2x, x+y−1

⚠️ 자주 하는 실수
• 3x²의 차수는 2 (3이 아님!)
• 계수는 부호 포함: −5x²y의 계수는 −5
• 상수항(숫자만 있는 항)의 차수는 0`,
    keyPoints: [
      '단항식: 수와 문자의 곱으로만 이루어진 식',
      '계수: 문자 앞의 수 (부호 포함)',
      '차수: 모든 문자 지수의 합',
      '상수항의 차수는 0',
    ],
    examples: [
      {
        question: '다음 단항식에서 계수와 차수를 구하시오.\n① −4x³  ② 2x²y  ③ 7',
        answer: '① 계수:−4, 차수:3\n② 계수:2, 차수:3 (2+1)\n③ 계수:7, 차수:0 (상수)',
        explanation: '① −4x³: 문자 앞 수=−4(계수), x의 지수=3(차수)\n② 2x²y: 계수=2, x지수2+y지수1=3(차수)\n③ 7: 계수=7, 문자 없으므로 차수=0',
      },
      {
        question: '3x²y와 같은 차수인 단항식을 고르시오.\n① 5xy  ② −2x²  ③ x²y  ④ 4x³',
        answer: '③ x²y, ④ 4x³ (둘 다 차수 3)',
        explanation: 'x²y: x지수2+y지수1=3, 4x³: 지수3. 모두 3차.',
      },
    ],
    practice: {
      question: '다음을 구하시오.\n① −6x²y³의 계수와 차수\n② ax²y가 4차단항식이 되려면 a에 대한 조건은?',
      answer: '① 계수:−6, 차수:5 (2+3)\n② y의 지수=1이므로 x²y는 3차 → 4차가 되려면 ax²y가 아니라 ax²y²처럼 y지수가 2여야. a는 0이 아닌 수.',
      hint: '① 차수: x의 지수 + y의 지수 합산\n② 차수 = x지수 + y지수 + ... 를 4로 만들기',
      steps: [
        '① x의 지수=2, y의 지수=3 → 차수=2+3=5',
        '① 계수=−6 (문자 앞의 수, 부호 포함)',
        '② ax²y에서 x지수=2, y지수=1 → 3차단항식',
        '② 4차가 되려면 지수 합이 4여야 하므로 a≠0이고 추가 문자 필요',
      ],
    },
    misconceptions: ['AM01'],
  },
  {
    id: 'AA02',
    title: '동류항과 다항식의 덧셈·뺄셈',
    subtitle: '동류항을 찾아 다항식을 계산한다',
    icon: '➕',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    textColor: 'text-blue-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['동류항', '다항식', '덧셈·뺄셈'],
    objective: '동류항의 뜻을 알고 다항식의 덧셈·뺄셈을 계산할 수 있다.',
    concept: `**동류항(Like Terms)**이란 문자와 차수가 모두 같은 항을 말합니다.

📌 동류항 조건
• 같은 문자 & 같은 지수 → 동류항 ✓
• 예: 3x²y와 −5x²y → 동류항 (x²y 형태 동일)
• 비예: 3x²y와 3xy² → 비동류항 (지수 배분 다름)

📌 다항식의 덧셈·뺄셈
①  괄호 제거 (뺄셈이면 부호 반전)
② 동류항끼리 묶기
③ 계수 계산

📌 뺄셈 주의사항
• A − (B + C) = A − B − C ← 부호 반전!
• A − (B − C) = A − B + C ← C의 부호도 반전!

📌 계산 예시
(3x² + 2x − 1) − (x² − 3x + 4)
= 3x² + 2x − 1 − x² + 3x − 4   ← 부호 반전
= (3−1)x² + (2+3)x + (−1−4)
= 2x² + 5x − 5`,
    keyPoints: [
      '동류항: 문자 종류 + 지수가 모두 같은 항',
      '뺄셈: 빼는 괄호의 모든 부호를 반전',
      '동류항끼리 계수를 더하거나 뺌',
      '정리 후 차수 높은 항부터 쓰기',
    ],
    examples: [
      {
        question: '(4x² − 3x + 2) + (−2x² + 5x − 7)을 계산하시오.',
        answer: '2x² + 2x − 5',
        explanation: '(4−2)x² = 2x²\n(−3+5)x = 2x\n(2−7) = −5\n→ 2x² + 2x − 5',
      },
      {
        question: '(5x² + 2x − 3) − (3x² − 4x + 1)을 계산하시오.',
        answer: '2x² + 6x − 4',
        explanation: '5x²+2x−3 − 3x²+4x−1 (부호 반전)\n= (5−3)x² + (2+4)x + (−3−1)\n= 2x² + 6x − 4',
      },
    ],
    practice: {
      question: '다음을 계산하시오.\n① 7a − 3a + 2a\n② (2x² − x + 3) − (x² + 2x − 5)',
      answer: '① 6a\n② x² − 3x + 8',
      hint: '① 동류항끼리 계수 합산\n② 뒤 괄호 부호 반전 후 동류항 정리',
      steps: [
        '① (7−3+2)a = 6a',
        '② 2x²−x+3 − x²−2x+5 (부호 반전)',
        '② (2−1)x² + (−1−2)x + (3+5)',
        '② = x² − 3x + 8',
      ],
    },
    misconceptions: ['AM02'],
  },
  {
    id: 'AA03',
    title: '단항식의 곱셈·나눗셈',
    subtitle: '지수 법칙을 이용한 단항식 계산',
    icon: '✖️',
    color: 'bg-purple-500',
    lightColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
    textColor: 'text-purple-700',
    difficulty: '보통',
    difficultyColor: 'bg-amber-100 text-amber-700',
    tags: ['지수법칙', '곱셈', '나눗셈'],
    objective: '지수 법칙을 이해하고 단항식의 곱셈·나눗셈을 계산할 수 있다.',
    concept: `**지수 법칙(Law of Exponents)**

📌 핵심 3가지 법칙
① 곱셈: xᵐ × xⁿ = xᵐ⁺ⁿ (지수 더하기)
② 나눗셈: xᵐ ÷ xⁿ = xᵐ⁻ⁿ (지수 빼기, m>n)
③ 거듭제곱: (xᵐ)ⁿ = xᵐⁿ (지수 곱하기)

📌 단항식 곱셈 방법
• 계수끼리 곱하기
• 같은 문자끼리 지수 더하기
• 예: 3x² × 4x³ = (3×4) × x^(2+3) = 12x⁵

📌 단항식 나눗셈 방법
• 계수끼리 나누기
• 같은 문자끼리 지수 빼기
• 예: 12x⁵ ÷ 4x² = (12÷4) × x^(5−2) = 3x³

📌 거듭제곱 포함
• (2x³)² = 2² × (x³)² = 4x⁶
• (ab)ⁿ = aⁿbⁿ (분배 적용)

⚠️ 주의
• (−3)² = 9 (양수!), −3² = −9 (음수!)
• 왼쪽부터 순서대로: ÷, × 혼합 시 왼쪽부터`,
    keyPoints: [
      '곱셈: 계수×계수, 같은 문자 지수 더하기',
      '나눗셈: 계수÷계수, 같은 문자 지수 빼기',
      '거듭제곱: (xᵐ)ⁿ = xᵐⁿ',
      '음수 거듭제곱: (−a)² = a² (짝수 → 양수)',
    ],
    examples: [
      {
        question: '다음을 계산하시오.\n① 3x² × (−2x³)\n② 15a³b² ÷ 3ab',
        answer: '① −6x⁵\n② 5a²b',
        explanation: '① 3×(−2)=−6, x²×x³=x⁵ → −6x⁵\n② 15÷3=5, a³÷a=a², b²÷b=b → 5a²b',
      },
      {
        question: '(−2xy²)³을 계산하시오.',
        answer: '−8x³y⁶',
        explanation: '(−2)³=−8, x¹×³=x³, y²×³=y⁶\n→ −8x³y⁶ (홀수 거듭제곱 → 음수 유지)',
      },
    ],
    practice: {
      question: '다음을 계산하시오.\n① (−3a²b)² ÷ 9ab\n② 4x³y × (−2xy²) ÷ 8x²y',
      answer: '① ab\n② −xy²',
      hint: '① 거듭제곱 먼저, 그 다음 나눗셈\n② 왼쪽부터 차례로 계산',
      steps: [
        '① (−3a²b)² = 9a⁴b²',
        '① 9a⁴b² ÷ 9ab = a³b... 잠깐, 다시: 9a⁴b² ÷ 9ab = (9÷9)(a⁴÷a)(b²÷b) = a³b',
        '② 4x³y × (−2xy²) = −8x⁴y³',
        '② −8x⁴y³ ÷ 8x²y = (−8÷8)(x⁴÷x²)(y³÷y) = −x²y²',
      ],
    },
    misconceptions: ['AM03'],
  },
  {
    id: 'AA04',
    title: '다항식의 금셈 (분배법칙)',
    subtitle: '단항식과 다항식의 곱, 전개',
    icon: '📦',
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
    textColor: 'text-emerald-700',
    difficulty: '보통',
    difficultyColor: 'bg-amber-100 text-amber-700',
    tags: ['분배법칙', '전개', '다항식 곱셈'],
    objective: '분배법칙을 이용하여 단항식과 다항식의 곱을 전개할 수 있다.',
    concept: `**분배법칙(Distributive Law)**

📌 기본 형태
a(b + c) = ab + ac
a(b − c) = ab − ac

📌 단항식 × 다항식
• 2x(3x − 4) = 2x × 3x + 2x × (−4) = 6x² − 8x

📌 다항식 ÷ 단항식
• (6x² − 4x) ÷ 2x = 6x²/2x − 4x/2x = 3x − 2

📌 혼합 계산 순서
① 거듭제곱
② 단항식 × 다항식 (분배법칙)
③ 동류항 정리

📌 식의 값 구하기
• 식을 먼저 단순화 → 값 대입
• 예: x=3일 때 2x(x−1)
  = 2(3)(3−1) = 6 × 2 = 12`,
    keyPoints: [
      'a(b+c) = ab+ac (분배법칙)',
      '단항식 나누기: 각 항을 단항식으로 나누기',
      '전개 후 동류항 정리',
      '식의 값: 먼저 간소화 후 대입',
    ],
    examples: [
      {
        question: '다음을 전개하시오.\n① 3x(2x − 5)\n② −2ab(a + 3b − 1)',
        answer: '① 6x² − 15x\n② −2a²b − 6ab² + 2ab',
        explanation: '① 3x×2x=6x², 3x×(−5)=−15x → 6x²−15x\n② (−2ab)×a=−2a²b, (−2ab)×3b=−6ab², (−2ab)×(−1)=2ab',
      },
      {
        question: '(12x³ − 8x² + 4x) ÷ (−4x)를 계산하시오.',
        answer: '−3x² + 2x − 1',
        explanation: '12x³÷(−4x)=−3x², −8x²÷(−4x)=2x, 4x÷(−4x)=−1\n→ −3x² + 2x − 1',
      },
    ],
    practice: {
      question: '다음을 계산하시오.\n① 5x(x − 2) − 3x(2x + 1)\n② A = x−2, B = 2x+1일 때 2A−B를 x로 나타내기',
      answer: '① −x² − 13x\n② −7',
      hint: '① 각각 분배법칙 후 동류항 정리\n② 식에 대입 후 동류항 정리',
      steps: [
        '① 5x(x−2) = 5x² − 10x',
        '① 3x(2x+1) = 6x² + 3x',
        '① 5x²−10x − (6x²+3x) = −x² − 13x',
        '② 2(x−2) − (2x+1) = 2x−4−2x−1 = −5',
      ],
    },
    misconceptions: ['AM04'],
  },
  {
    id: 'AA05',
    title: '다항식의 활용',
    subtitle: '식의 값 계산 및 종합 활용',
    icon: '🎯',
    color: 'bg-rose-500',
    lightColor: 'bg-rose-50',
    borderColor: 'border-rose-300',
    textColor: 'text-rose-700',
    difficulty: '심화',
    difficultyColor: 'bg-red-100 text-red-700',
    tags: ['식의 값', '종합', '활용'],
    objective: '다항식 계산을 종합적으로 활용하고 식의 값을 구할 수 있다.',
    concept: `**다항식 종합 활용**

📌 식의 값 계산 절차
① 식 간소화 (전개·정리)
② 음수 대입 시 반드시 괄호
③ 계산 순서: 거듭제곱 → 곱셈/나눗셈 → 덧셈/뺄셈

📌 식의 대입
• A = 3x−1이면 2A = 2(3x−1) = 6x−2
• 잘못된 식을 복원하는 문제도 자주 출제!

📌 가장 높은 차항 찾기
• 다항식의 차수: 가장 높은 항의 차수
• 최고차항의 계수: 가장 높은 차수의 계수

📌 자주 출제되는 유형
① "잘못 계산" 문제: 역으로 원래 식 복원
② 조건 주어진 대입 문제
③ 계수 비교 문제

⚠️ 실수 포인트
• x=−3 대입: 반드시 (−3) 괄호 사용
• 2x²에 x=−3: 2×(−3)² = 2×9 = 18`,
    keyPoints: [
      '음수 대입 시 반드시 괄호 사용',
      '다항식 차수 = 가장 높은 항의 차수',
      '식 대입 시 괄호로 치환',
      '잘못 계산 문제: 역산으로 원래 식 찾기',
    ],
    examples: [
      {
        question: 'x=−2, y=3일 때 2x²y − xy + x의 값을 구하시오.',
        answer: '32',
        explanation: '2(−2)²(3) − (−2)(3) + (−2)\n= 2(4)(3) + 6 − 2\n= 24 + 6 − 2\n= 28\n\n재계산: 2×4×3=24, −(−2)(3)=6, +(−2)=−2\n24+6−2=28',
      },
      {
        question: 'A = 2x−1, B = x+3일 때, 3A − 2B를 간단히 하시오.',
        answer: '4x − 9',
        explanation: '3(2x−1) − 2(x+3)\n= 6x − 3 − 2x − 6\n= 4x − 9',
      },
    ],
    practice: {
      question: '어떤 다항식에서 x²−2x+1을 빼야 할 것을 잘못하여 더했더니\n3x²+x−3이 되었다. 올바른 계산 결과를 구하시오.',
      answer: 'x² + 5x − 5',
      hint: '"더했더니" → 원래 식 = 결과 − 잘못 더한 식',
      steps: [
        '원래 식 = (3x²+x−3) − (x²−2x+1)',
        '= 3x²+x−3 − x²+2x−1',
        '= 2x²+3x−4 (원래 식)',
        '올바른 계산: (2x²+3x−4) − (x²−2x+1)',
        '= 2x²+3x−4 − x²+2x−1',
        '= x² + 5x − 5',
      ],
    },
    misconceptions: ['AM04', 'AM02'],
  },
]

const TOTAL_LEARN_STEPS = STEPS.length
const UNIT_ID = 'A2-1'

function PracticeBox({ practice, stepId }: { practice: typeof STEPS[0]['practice'], stepId: string }) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  return (
    <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-indigo-600 mb-2 flex items-center gap-1">
        <PenTool size={12} /> 연습 문제
      </p>
      <p className="text-sm text-slate-800 font-medium mb-3 whitespace-pre-line">{practice.question}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold transition-colors">
          💡 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold transition-colors">
          📋 풀이 단계
        </button>
        <button onClick={() => setShowAnswer(!showAnswer)}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-semibold transition-colors">
          ✅ 정답 확인
        </button>
      </div>
      {showHint && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 {practice.hint}
        </div>
      )}
      {showSteps && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-bold text-blue-700 mb-2">단계별 풀이:</p>
          {practice.steps.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs text-blue-800 mb-1">
              <span className="font-bold text-blue-500 w-4">{i + 1}.</span>
              <span className="font-mono">{s}</span>
            </div>
          ))}
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-mono whitespace-pre-line">
          {practice.answer}
        </div>
      )}
    </div>
  )
}

function ExampleCard({ example, idx }: { example: typeof STEPS[0]['examples'][0], idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-sm font-semibold text-slate-700">예제 {idx + 1}: {example.question.slice(0, 28)}...</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-800 whitespace-pre-line">{example.question}</p>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs font-bold text-emerald-600 mb-1">풀이</p>
            <p className="text-sm font-mono text-emerald-800 whitespace-pre-line">{example.answer}</p>
          </div>
          <p className="text-xs text-slate-500 italic whitespace-pre-line">{example.explanation}</p>
        </div>
      )}
    </div>
  )
}

function StoryCard({ story }: { story: StepStory }) {
  const [lineIdx, setLineIdx] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const speakerColor: Record<string, string> = {
    student: 'bg-blue-100 text-blue-800',
    teacher: 'bg-purple-100 text-purple-800',
    friend: 'bg-green-100 text-green-800',
    narrator: 'bg-amber-100 text-amber-800',
  }
  return (
    <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-2xl p-4">
      <p className="text-xs text-slate-500 italic mb-3">{story.scene}</p>
      <div className="min-h-[80px] mb-3">
        {story.lines.slice(0, lineIdx + 1).map((line, i) => (
          <div key={i} className={`flex gap-2 mb-2 ${i < lineIdx ? 'opacity-50' : ''}`}>
            <span className="text-lg flex-shrink-0">{line.emoji}</span>
            <div>
              {line.name && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-1 ${speakerColor[line.speaker]}`}>
                  {line.name}
                </span>
              )}
              <span className="text-sm text-slate-700">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1 bg-slate-200 rounded-full">
          <div className="h-1 bg-indigo-400 rounded-full transition-all"
            style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-400">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      <div className="flex gap-2">
        {lineIdx < story.lines.length - 1 ? (
          <button onClick={() => setLineIdx(i => i + 1)}
            className="flex-1 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1">
            <ChevronRight size={13} /> 다음
          </button>
        ) : (
          <button onClick={() => setRevealed(true)}
            className="flex-1 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1">
            <Sparkles size={13} /> 핵심 개념 보기
          </button>
        )}
        <button onClick={() => { setLineIdx(0); setRevealed(false) }}
          className="px-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-500 hover:bg-slate-50 transition-colors">
          처음
        </button>
      </div>
      {revealed && (
        <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-xl flex items-start gap-2">
          <span className="text-xl">{story.revealEmoji}</span>
          <p className="text-xs text-indigo-800 font-medium leading-relaxed">{story.reveal}</p>
        </div>
      )}
    </div>
  )
}

function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const visible = fragments.filter(f => !dismissed.has(f.fragment.id))
  if (visible.length === 0) return null
  return (
    <div className="card border border-amber-200 bg-amber-50">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-amber-500" />
        <p className="text-sm font-bold text-amber-800">AI 예상 파편</p>
        <span className="ml-auto text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-bold">
          {visible.length}개
        </span>
      </div>
      <div className="space-y-2">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-start gap-2 p-2 rounded-xl border ${cfg.color}`}>
              <span className="text-base">{f.fragment.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-bold ${cfg.color}`}>{f.fragment.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{f.fragment.misconceptionLabel}</p>
              </div>
              <button onClick={() => setDismissed(s => new Set([...s, f.fragment.id]))}
                className="text-slate-300 hover:text-slate-500 flex-shrink-0">
                <X size={12} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface A2_1LearnPageProps { defaultStepId?: string | null }

export default function A2_1LearnPage({ defaultStepId }: A2_1LearnPageProps) {
  const [selectedStep, setSelectedStep] = useState<typeof STEPS[0] | null>(
    defaultStepId ? STEPS.find(s => s.id === defaultStepId) ?? null : null
  )
  const [activeTab, setActiveTab] = useState<'story' | 'concept' | 'examples' | 'practice'>('story')
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)

  const { completeStep } = useProgressStore(s => ({ completeStep: s.completeStep }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const learnProgress = useLearnProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  const completedCount = learnProgress.completedCount
  const progressPct = learnProgress.pct

  useEffect(() => {
    if (defaultStepId) {
      const step = STEPS.find(s => s.id === defaultStepId)
      if (step) { setSelectedStep(step); setActiveTab('concept') }
    }
  }, [defaultStepId])

  const handleComplete = (step: typeof STEPS[0]) => {
    completeStep(UNIT_ID, step.id, step.title)
    setSelectedStep(null)
  }

  if (!selectedStep) {
    return (
      <div className="p-4 space-y-4">
        <div className="card bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
              <span className="text-white text-xl font-black">식</span>
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-800">단항식과 다항식</h2>
              <p className="text-xs text-slate-500">A2-1 · 중학교 1학년 문자와 식</p>
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-slate-500">
              <span>학습 진도</span>
              <span className="font-bold text-indigo-600">{completedCount}/{TOTAL_LEARN_STEPS} 완료</span>
            </div>
            <div className="h-2.5 bg-indigo-100 rounded-full overflow-hidden">
              <div className="h-2.5 bg-gradient-to-r from-indigo-400 to-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        </div>

        <PredictedFragmentPanel fragments={predictedFragments} />

        <div className="space-y-3">
          {STEPS.map((step, idx) => {
            const sp = learnProgress[step.id]
            const isDone = sp?.status === 'completed' || sp?.status === 'mastered'
            const video = getApprovedByTag(step.id)
            return (
              <div key={step.id}
                className={`card border-2 cursor-pointer transition-all hover:shadow-md ${isDone ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 hover:border-indigo-200'}`}
                onClick={() => { setSelectedStep(step); setActiveTab('story') }}>
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-emerald-100' : step.lightColor}`}>
                    {isDone ? <CheckCircle size={22} className="text-emerald-500" /> : <span className="text-xl">{step.icon}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${step.difficultyColor}`}>{step.difficulty}</span>
                      {video && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                          <Youtube size={10} /> 영상
                        </span>
                      )}
                    </div>
                    <p className={`text-sm font-bold ${isDone ? 'text-emerald-700' : 'text-slate-800'}`}>
                      {idx + 1}. {step.title}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{step.subtitle}</p>
                  </div>
                  <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
                </div>
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {step.tags.map(t => (
                    <span key={t} className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const story = STEP_STORIES[selectedStep.id]
  const sp = learnProgress[selectedStep.id]
  const isDone = sp?.status === 'completed' || sp?.status === 'mastered'
  const video = getApprovedByTag(selectedStep.id)

  const tabs = [
    { key: 'story' as const, label: '스토리', icon: <MessageCircle size={13} /> },
    { key: 'concept' as const, label: '개념', icon: <BookOpen size={13} /> },
    { key: 'examples' as const, label: '예제', icon: <FileText size={13} /> },
    { key: 'practice' as const, label: '연습', icon: <PenTool size={13} /> },
  ]

  return (
    <div className="p-4 space-y-4">
      <button onClick={() => setSelectedStep(null)}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors">
        ← 목록으로
      </button>

      <div className={`card border-2 ${selectedStep.borderColor} ${selectedStep.lightColor}`}>
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-xl ${selectedStep.color} flex items-center justify-center flex-shrink-0`}>
            <span className="text-white text-xl">{selectedStep.icon}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selectedStep.difficultyColor}`}>{selectedStep.difficulty}</span>
              <span className="text-xs text-slate-400">{selectedStep.id}</span>
              {isDone && <CheckCircle size={14} className="text-emerald-500" />}
            </div>
            <h3 className="text-base font-black text-slate-800">{selectedStep.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{selectedStep.subtitle}</p>
          </div>
        </div>
        <div className="mt-3 p-3 bg-white/70 rounded-xl border border-white">
          <div className="flex items-center gap-1.5 mb-1">
            <Target size={12} className={selectedStep.textColor} />
            <p className="text-xs font-bold text-slate-600">학습 목표</p>
          </div>
          <p className="text-xs text-slate-700 leading-relaxed">{selectedStep.objective}</p>
        </div>
        {video && (
          <button onClick={() => setVideoModal({ stepId: selectedStep.id, label: selectedStep.title })}
            className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs font-bold hover:bg-red-100 transition-colors">
            <Youtube size={13} /> 개념 영상 보기
          </button>
        )}
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === tab.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'story' && story && <StoryCard story={story} />}

      {activeTab === 'concept' && (
        <div className="card space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Brain size={15} className={selectedStep.textColor} />
            <h4 className="text-sm font-bold text-slate-800">핵심 개념</h4>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-700 leading-relaxed whitespace-pre-wrap font-mono">
            {selectedStep.concept}
          </div>
          <div className="space-y-2">
            <p className="text-xs font-bold text-slate-600 flex items-center gap-1">
              <Star size={12} className="text-amber-400" /> 핵심 포인트
            </p>
            {selectedStep.keyPoints.map((kp, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                <CheckCircle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-700">{kp}</p>
              </div>
            ))}
          </div>
          {selectedStep.misconceptions.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertTriangle size={12} className="text-red-500" />
                <p className="text-xs font-bold text-red-600">흔한 오개념 주의</p>
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedStep.misconceptions.map(code => (
                  <span key={code} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-mono">{code}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'examples' && (
        <div className="space-y-3">
          {selectedStep.examples.map((ex, i) => (
            <ExampleCard key={i} example={ex} idx={i} />
          ))}
        </div>
      )}

      {activeTab === 'practice' && (
        <PracticeBox practice={selectedStep.practice} stepId={selectedStep.id} />
      )}

      {!isDone ? (
        <button onClick={() => handleComplete(selectedStep)}
          className={`w-full py-3 ${selectedStep.color} hover:opacity-90 text-white font-bold rounded-2xl transition-all flex items-center justify-center gap-2 text-sm`}>
          <CheckCircle size={16} /> 이 단계 완료하기
        </button>
      ) : (
        <div className="w-full py-3 bg-emerald-100 text-emerald-700 font-bold rounded-2xl flex items-center justify-center gap-2 text-sm border border-emerald-200">
          <CheckCircle size={16} /> 완료한 단계입니다 ✓
        </div>
      )}

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
